import { useCallback, useEffect, useRef, useState } from 'react';

export interface ApiResource<T> {
  data: T | null;
  error: Error | null;
  loading: boolean;
  /** True while a write is in flight — the UI stays interactive but can dim. */
  busy: boolean;
  reload: () => Promise<void>;
  /**
   * Run a write that returns the fresh snapshot and adopt it.
   * Errors are captured into `error` and re-thrown for callers that care.
   */
  mutate: (fn: () => Promise<T>) => Promise<T | null>;
  clearError: () => void;
}

/**
 * One resource, one screen. Both portals load a single snapshot and every
 * mutation returns the next one, so there is no cache to invalidate and no
 * chance of two components disagreeing about the same number.
 */
export function useApiResource<T>(load: () => Promise<T>, enabled = true): ApiResource<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [busy, setBusy] = useState(false);
  const alive = useRef(true);
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const next = await loadRef.current();
      if (alive.current) { setData(next); setError(null); }
    } catch (e) {
      if (alive.current) setError(e as Error);
    } finally {
      if (alive.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) { setLoading(false); setData(null); return; }
    void reload();
  }, [enabled, reload]);

  const mutate = useCallback(async (fn: () => Promise<T>) => {
    setBusy(true);
    try {
      const next = await fn();
      if (alive.current) { setData(next); setError(null); }
      return next;
    } catch (e) {
      if (alive.current) setError(e as Error);
      throw e;
    } finally {
      if (alive.current) setBusy(false);
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return { data, error, loading, busy, reload, mutate, clearError };
}
